import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { projectApi } from "@/services/projectService";
import { userApi } from "@/services/userService";
import { ServiceType } from "@/enums/project.enum";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Save, Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Roles } from "@/enums/roles.enum";
import { ahjApi } from "@/services/ahjService";
import { IAHJ } from "@/types/ahj";
import { AHJFormDialog } from "@/components/ahj/AHJFormDialog";
import { useState } from "react";

interface FormValues {
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  buildingOwner: string;
  buildingOwnerAddress: string;
  service: ServiceType;
  permitNumber?: string;
  estimatedReviewDateByAHJ?: string; // ISO string
  projectManager?: string; // User ID
  ahj?: string; // AHJ ID
}

const services = [
  { value: ServiceType.PERMITTING_DRAWING_AND_SUBMITTAL, label: "Permitting Service: Drawing & Submittal" },
  { value: ServiceType.PERMITTING_DRAWING_ONLY, label: "Permitting Service: Drawing Only" },
  { value: ServiceType.PERMITTING_SUBMITTAL_ONLY, label: "Permitting Service: Submittal Only" },
];

const EditProject = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const qc = useQueryClient();
  const { user } = useAuth();

  const { data: project, isLoading } = useQuery({
    queryKey: ["projects", id],
    queryFn: () => projectApi.getProject(id!),
    enabled: !!id,
  });

  // Fetch all users for project manager selection (Admin only)
  const { data: usersData } = useQuery({
    queryKey: ["users"],
    queryFn: userApi.getUsers,
    enabled: user?.role === Roles.ADMIN,
  });

  // Filter to only show Admin users as project managers
  const adminUsers = usersData?.users.filter(u => u.role === Roles.ADMIN) || [];

  // Fetch all AHJs
  const { data: ahjsData } = useQuery({
    queryKey: ["ahjs"],
    queryFn: () => ahjApi.getAHJs({}),
  });

  const [ahjDialogOpen, setAhjDialogOpen] = useState(false);

  const handleAHJCreated = (ahj: IAHJ) => {
    setValue("ahj", ahj._id);
    setAhjDialogOpen(false);
  };

  const { register, handleSubmit, reset, setValue } = useForm<FormValues>();

  useEffect(() => {
    if (project) {
      reset({
        name: project.name,
        address: project.address,
        city: project.city,
        state: project.state,
        zip: project.zip,
        buildingOwner: project.buildingOwner,
        buildingOwnerAddress: project.buildingOwnerAddress,
        service: project.service,
        permitNumber: project.permitNumber,
        estimatedReviewDateByAHJ: project.estimatedReviewDateByAHJ ? new Date(project.estimatedReviewDateByAHJ).toISOString().substring(0,10) : undefined,
        projectManager: project.projectManager?._id || undefined,
        ahj: project.ahj?._id || undefined,
      });
    }
  }, [project, reset]);

  const { mutate: updateProject, isPending: saving } = useMutation({
    mutationFn: (dto: Partial<FormValues>) => projectApi.updateProject(id!, {
      ...dto,
      estimatedReviewDateByAHJ: dto.estimatedReviewDateByAHJ ? new Date(dto.estimatedReviewDateByAHJ).toISOString() : undefined,
    }),
    onSuccess: () => {
      toast({ title: "Project updated" });
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["projects", id] });
      navigate(`/projects/${id}`);
    },
    onError: () => toast({ title: "Update failed", variant: "destructive" }),
  });

  const onSubmit = (data: FormValues) => {
    updateProject(data);
  };

  if (isLoading || !project) return <div className="p-6">Loading...</div>;

  return (
    <div className="container max-w-4xl py-8 space-y-8 animate-in fade-in duration-500">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="h-8 w-8 -ml-2 hover:bg-accent hover:text-accent-foreground rounded-full"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">Edit Project</h1>
          </div>
          <p className="text-muted-foreground pl-8">
            Update the project details and settings.
          </p>
        </div>
      </div>

      <Card className="border-border/50 shadow-sophisticated highlight-top overflow-hidden">
        <CardHeader className="bg-muted/30 border-b border-border/50 pb-6">
          <CardTitle className="text-xl">Project Details</CardTitle>
          <CardDescription>Update project information below.</CardDescription>
        </CardHeader>
        <CardContent className="p-6 pt-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground/80">Project Name</Label>
                <Input className="bg-background/50 focus:bg-background transition-colors" {...register("name")} />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground/80">Service</Label>
                <Select defaultValue={project.service} onValueChange={(v) => setValue("service", v as ServiceType)}>
                  <SelectTrigger className="bg-background/50 focus:bg-background transition-colors">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground/80">Building Owner</Label>
                <Input className="bg-background/50 focus:bg-background transition-colors" {...register("buildingOwner")} />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground/80">Building Owner Address</Label>
                <Input className="bg-background/50 focus:bg-background transition-colors" {...register("buildingOwnerAddress")} />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label className="text-sm font-medium text-foreground/80">Address</Label>
                <Input className="bg-background/50 focus:bg-background transition-colors" {...register("address")} />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground/80">City</Label>
                <Input className="bg-background/50 focus:bg-background transition-colors" {...register("city")} />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground/80">State</Label>
                <Input className="bg-background/50 focus:bg-background transition-colors" {...register("state")} />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground/80">Zip</Label>
                <Input className="bg-background/50 focus:bg-background transition-colors" {...register("zip")} />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground/80">Permit #</Label>
                <Input className="bg-background/50 focus:bg-background transition-colors" {...register("permitNumber")} placeholder="Enter permit number" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground/80">Estimated Review Date by AHJ</Label>
                <Input className="bg-background/50 focus:bg-background transition-colors" type="date" {...register("estimatedReviewDateByAHJ")} />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground/80">AHJ</Label>
                <Select 
                  defaultValue={project?.ahj?._id || "none"} 
                  onValueChange={(v) => {
                    if (v === "add-new") {
                      setAhjDialogOpen(true);
                    } else {
                      setValue("ahj", v === "none" ? undefined : v);
                    }
                  }}
                >
                  <SelectTrigger className="bg-background/50 focus:bg-background transition-colors">
                    <SelectValue placeholder="Select AHJ" />
                  </SelectTrigger>
                  <SelectContent>
                    <div className="sticky top-0 bg-background border-b z-10">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setAhjDialogOpen(true);
                        }}
                        className="w-full px-2 py-2 text-sm font-medium text-primary hover:bg-accent flex items-center gap-2 transition-colors"
                      >
                        <Plus className="h-4 w-4" />
                        Add New AHJ
                      </button>
                    </div>
                    <SelectItem value="none">None</SelectItem>
                    {Array.from(
                      (ahjsData?.ahjs || [])
                        .sort((a, b) => {
                          if (a.type !== b.type) return a.type.localeCompare(b.type);
                          if (a.state !== b.state) return a.state.localeCompare(b.state);
                          return a.name.localeCompare(b.name);
                        })
                        .reduce((acc, ahj) => {
                          const groupKey = `${ahj.type}-${ahj.state}`;
                          if (!acc.has(groupKey)) {
                            acc.set(groupKey, []);
                          }
                          acc.get(groupKey)?.push(ahj);
                          return acc;
                        }, new Map<string, IAHJ[]>())
                        .entries()
                    ).map(([groupKey, ahjs]) => {
                      const [type, state] = groupKey.split('-');
                      return (
                        <div key={groupKey}>
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">
                            {type.charAt(0).toUpperCase() + type.slice(1)} - {state}
                          </div>
                          {ahjs.map((ahj: IAHJ) => (
                            <SelectItem key={ahj._id} value={ahj._id}>
                              {ahj.name} ({ahj.state})
                            </SelectItem>
                          ))}
                        </div>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              {user?.role === Roles.ADMIN && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-foreground/80">QWS Project Manager</Label>
                  <Select 
                    defaultValue={project?.projectManager?._id || "none"} 
                    onValueChange={(v) => setValue("projectManager", v === "none" ? undefined : v)}
                  >
                    <SelectTrigger className="bg-background/50 focus:bg-background transition-colors">
                      <SelectValue placeholder="Select project manager (Admin only)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {adminUsers.map(u => (
                        <SelectItem key={u._id} value={u._id}>
                          {`${u.firstName} ${u.lastName}`.trim() || u.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-border/50">
              <Button type="button" variant="outline" onClick={() => navigate(`/projects/${id}`)} className="hover:bg-accent hover:text-accent-foreground transition-colors">Cancel</Button>
              <Button type="submit" disabled={saving} className="btn-gradient flex items-center shadow-md hover:shadow-lg transition-all">
                <Save className="h-4 w-4 mr-2" /> {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      <AHJFormDialog
        open={ahjDialogOpen}
        onOpenChange={setAhjDialogOpen}
        onSuccess={handleAHJCreated}
      />
    </div>
  );
};

export default EditProject;
